import { type ReactNode } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';

import { EntitySectionLayout } from '@/components/composed/EntitySectionLayout';
import { DateCell } from '@/components/composed/DateCell';
import { IdCell } from '@/components/composed/IdCell';
import { MaskedCell } from '@/components/composed/MaskedCell';
import { StatusBadge } from '@/components/composed/StatusBadge';
import { EmptyState } from '@/components/composed/EmptyState';
import { resolveSection } from '@/lib/entity-sections';
import { generalAgentDetailQuery } from '../api/general-agent-queries';
import type { GeneralAgentId } from '@/lib/api/ids';
import { generalAgentSections } from '../general-agent-sections';

/*
 * GeneralAgentDetail — clone of ProviderDetail/AgentBrokerDetail, a consumer of
 * the composed EntitySectionLayout.
 *
 * The route resolves the active section from the `$section` param and PUBLISHES
 * the entity sub-nav into the shell rail (via `useRailSubNav`). This
 * component owns only the domain wiring left in the content column: resolving
 * which body to render for the active section. It is navigation-mechanism-
 * agnostic — it never imports a router.
 */
export function GeneralAgentDetail({
  generalAgentId,
  section,
}: {
  generalAgentId: GeneralAgentId;
  /** Raw `$section` route param (may be missing/invalid). */
  section: string | undefined;
}) {
  const { data: generalAgent } = useSuspenseQuery(
    generalAgentDetailQuery(generalAgentId),
  );
  const activeSectionId =
    resolveSection(section, generalAgentSections) ??
    generalAgentSections[0]!.id;

  return (
    <EntitySectionLayout>
      <SectionBody sectionId={activeSectionId} generalAgent={generalAgent} />
    </EntitySectionLayout>
  );
}

function SectionBody({
  sectionId,
  generalAgent,
}: {
  sectionId: string;
  generalAgent: import('@/lib/api/domains/general-agents').GeneralAgent;
}) {
  if (sectionId === 'profile') {
    return (
      <dl className="grid grid-cols-2 gap-4 text-sm">
        <Field label="Name">
          <span className="font-medium">{generalAgent.name}</span>
        </Field>
        {generalAgent.dbaName ? (
          <Field label="DBA name">
            <span className="font-medium">{generalAgent.dbaName}</span>
          </Field>
        ) : null}
        {generalAgent.publicId ? (
          <Field label="Public ID">
            <IdCell value={generalAgent.publicId} />
          </Field>
        ) : null}
        {generalAgent.npn ? (
          <Field label="NPN">
            <span className="font-medium">{generalAgent.npn}</span>
          </Field>
        ) : null}
        {generalAgent.taxId ? (
          <Field label="TIN">
            <MaskedCell value={generalAgent.taxId} />
          </Field>
        ) : null}
        <Field label="Status">
          <StatusBadge
            label={generalAgent.status === 'active' ? 'Active' : 'Inactive'}
            tone={generalAgent.status === 'active' ? 'success' : 'neutral'}
          />
        </Field>
        <Field label="Created">
          <DateCell value={generalAgent.createdAt} />
        </Field>
      </dl>
    );
  }

  // The remaining sections (commissions, contracts, bank-accounts,
  // disbursement-schedules) have no FE contract yet — their endpoints are the
  // entity sub-domains that land in later slices. The layout + deep-link
  // routing is fully wired now; each section fills in when its data contract
  // exists.
  const label =
    generalAgentSectionLabel(sectionId) ?? sectionId.replace(/-/g, ' ');
  return (
    <EmptyState
      title={label}
      description="This section will be available in a later release."
    />
  );
}

function generalAgentSectionLabel(id: string): string | undefined {
  return generalAgentSections.find((s) => s.id === id)?.label;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{children}</dd>
    </div>
  );
}
